/**
 * Birthday stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const birthdayStickersPack: ToilePack = {
    slug: 'birthday-stickers',
    category: 'sticker',
    name: 'Birthday stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'cake', label: 'Cake', subject: 'A round character holding a cake with lit candles, no lettering'},
        {key: 'burst', label: 'Birthday burst', subject: 'A burst shape lettered "HAPPY BIRTHDAY" with confetti'},
        {key: 'horn', label: 'Party horn', subject: 'A round character in a party hat blowing a party horn, no lettering'},
    ],
    styles: [
        {key: 'chat', label: 'Chat sticker', style: 'messaging-app sticker, expressive cartoon, thick outline, bold flat colour', palette: 'Bright flat colour with a white outline', framing: 'The sticker isolated on a plain ground with a white outline, built to read at chat size'},
        {key: 'kawaii', label: 'Kawaii', style: 'kawaii sticker illustration, rounded shapes, tiny smiling faces, soft thick outlines', palette: 'Pastel pink, mint, lilac and cream with dark outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'doodle', label: 'Hand-drawn', style: 'hand-drawn marker doodle sticker, loose line, flat wash fills', palette: 'Two or three muted colours with a black line', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['for a first birthday', 'in a single pastel colour', 'as a milestone number instead'],
    directionHint: 'try: for a first birthday',
    seo: {
        title: 'Birthday Stickers: Cake, Happy Birthday Burst, Horn',
        metaDescription: 'Nine birthday stickers — a character with a cake, a lettered happy birthday burst, a party horn character — in chat, kawaii, and doodle styles.',
        h1: 'Birthday stickers: cake, happy birthday burst, and party horn',
        tagline: 'Nine birthday stickers, three party moments, three styles each.',
        intro: 'The birthday sticker: a character with a cake, a lettered happy birthday burst, a party horn character mid-blow. Rendered chat, kawaii, and doodle so the set fits a card, a chat thread, or a party favor alike. Ask for a name or age to work into the lettering, and the same three styles carry it.',
        useCases: [
            'a birthday card or gift topper',
            'a group chat birthday sticker',
            'a party favor bag sticker',
            'a scrapbook birthday page accent',
            'a printable birthday sticker sheet',
        ],
        faq: [
            {q: 'Can I add a name or age to the happy birthday burst?', a: 'Yes — the direction field takes a name, age, or short phrase, lettered in the same three styles.'},
            {q: "What's the difference between chat, kawaii, and doodle?", a: 'Chat is a rounded messaging-app character style, kawaii is softer and big-eyed, and doodle is a hand-drawn sketch look — pick whichever fits the card or party theme.'},
            {q: "Are these good for a kid's birthday party, not just cards?", a: 'Yes — the party-horn and cake designs work well as favor-bag stickers or decoration accents too.'},
            {q: 'Can I print a full sheet for a group chat celebration?', a: 'Yes — the set is sized to print as a full sheet or as single stickers for chat use.'},
            {q: "Can I use these for a party I'm planning for a client?", a: 'Personal and one-off party use is fine; check the license terms before reselling the designs themselves.'},
        ],
    },
}
